import React, { useEffect, useRef, useState } from "react";
import { api, getToken } from "../../../software/api";
import { toast } from "sonner";
import { Play, X, Loader2, Download, Monitor } from "lucide-react";

const TERM_KEY = "anx_spin_terminal";
const sampleFor = (type = "") => /salt|mineral|magna/i.test(type) ? "salt" : /bromine/i.test(type) ? "bromine" : /chlorine free|biguanide|baquacil/i.test(type) ? "biguanide" : "chlorine";

function OfflineNotice({ name, onSetup }) {
  return (
    <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3 space-y-1.5" data-testid="spin-offline-notice">
      <div className="font-black text-amber-800">"{name}" is already linked — its bridge just isn't running right now.</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>On the computer with the Spin Touch, make sure <b>WaterLink Connect</b> is open and the tester is plugged in.</li>
        <li>Double-click <b>Start Spin Bridge</b> on that computer's Desktop (or restart the computer — it starts automatically).</li>
        <li>Wait about 5 seconds, then click <b>Spin</b> again.</li>
      </ol>
      <button onClick={onSetup} data-testid="spin-show-setup" className="text-[11px] text-sky-700 underline">The bridge was never installed on that computer — show the one-time setup</button>
    </div>
  );
}

function ConnectPanel({ terminals, current, onPick, onClose }) {
  const [name, setName] = useState(current || "Front Counter");
  const known = terminals.find((t) => t.name === current && !t.online);
  const [setup, setSetup] = useState(!known);
  const download = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/spin/bridge.zip?base=${encodeURIComponent(window.location.origin)}&terminal=${encodeURIComponent(name)}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("Download failed");
      const a = document.createElement("a"); a.href = URL.createObjectURL(await res.blob()); a.download = "AqualuxSpinBridge.zip"; a.click();
      localStorage.setItem(TERM_KEY, name); onPick(name);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-center justify-center p-3" data-testid="spin-connect-modal">
      <div className="w-full max-w-lg bg-gradient-to-b from-white to-[#eaf3fb] border border-[#7ba7d9] rounded-xl shadow-2xl max-h-[94vh] overflow-y-auto">
        <div className="flex justify-between items-center px-4 py-2.5 border-b border-[#7ba7d9] bg-[#dbe9f7] rounded-t-xl">
          <span className="font-black text-[#123a63] flex items-center gap-2"><Monitor className="h-4 w-4" /> Connect LaMotte Spin Tester</span>
          <button onClick={onClose} data-testid="spin-connect-close" className="rounded-full p-1 hover:bg-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3 text-[13px] text-slate-700">
          {terminals.length > 0 && (
            <div data-testid="spin-terminal-list">
              <div className="font-black text-[#123a63] mb-1">Which Spin Tester is at THIS computer?</div>
              {terminals.map((t) => (
                <button key={t.name} onClick={() => { localStorage.setItem(TERM_KEY, t.name); onPick(t.name); }} data-testid={`spin-terminal-${t.name}`}
                  className={`w-full flex justify-between items-center px-3 py-2 mb-1 rounded-md border ${current === t.name ? "border-[#123a63] bg-white" : "border-[#7ba7d9] bg-white/60 hover:bg-white"}`}>
                  <span className="font-bold">{t.name}</span>
                  <span className={`text-[11px] font-black ${t.online && t.wlc_ok ? "text-emerald-700" : t.online ? "text-amber-700" : "text-slate-400"}`}>
                    {t.online ? (t.wlc_ok ? "● Ready" : "● Open WaterLink Connect") : "○ Offline"}
                  </span>
                </button>
              ))}
            </div>
          )}
          {known && <OfflineNotice name={known.name} onSetup={() => setSetup(true)} />}
          {setup && <div className="rounded-md border border-[#7ba7d9] bg-white/70 p-3 space-y-1.5" data-testid="spin-setup-steps">
            <div className="font-black text-[#123a63]">One-time setup on the computer the Spin Touch is plugged into</div>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Open <b>WaterLink Connect</b> and plug in the Spin Touch by USB.</li>
              <li>Install <b>Python</b> from <a href="https://www.python.org/downloads/" target="_blank" rel="noreferrer" className="text-sky-700 underline">python.org</a> and tick <b>"Add python.exe to PATH"</b>.</li>
              <li>Name this tester and download the bridge:</li>
            </ol>
            <div className="flex gap-2 pl-5">
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="spin-terminal-name" className="flex-1 border border-[#7ba7d9] rounded-sm px-2 py-1 bg-[#cfeaf5] font-bold outline-none focus:bg-white" />
              <button onClick={download} disabled={!name.trim()} data-testid="spin-download-bridge" className="px-3 py-1 rounded-sm bg-[#123a63] hover:bg-[#0d2c4b] text-white font-black flex items-center gap-1.5"><Download className="h-4 w-4" /> Download Bridge</button>
            </div>
            <ol start={4} className="list-decimal pl-5 space-y-1">
              <li>Unzip it to <b>Documents</b> and double-click <b>INSTALL.bat</b>. It then runs in the background, starts with Windows, and adds a <b>Start Spin Bridge</b> icon to the Desktop.</li>
            </ol>
          </div>}
          <p className="text-[11px] text-slate-500">Only the computer with the Spin Touch plugged in needs the bridge. Other computers can pick that tester above.</p>
        </div>
      </div>
    </div>
  );
}

const LIGHT = (t) => (t.online && t.wlc_ok ? ["bg-emerald-500", "Ready"] : t.online ? ["bg-amber-400", "Open WaterLink Connect"] : ["bg-red-500", "Offline"]);

function SpinLights({ terminals, mine, onOpen }) {
  if (!terminals.length) return <button onClick={() => onOpen("")} data-testid="spin-lights-none" className="flex items-center gap-1 text-[11px] font-bold text-slate-500"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />No Spin tester linked</button>;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="spin-lights">
      {terminals.map((t) => {
        const [c, label] = LIGHT(t);
        return (
          <button key={t.name} onClick={() => onOpen(t.name)} title={`${t.name}: ${label}`} data-testid={`spin-light-${t.name}`}
            className={`flex items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-[11px] font-bold transition-colors hover:bg-slate-50 ${t.name === mine ? "border-[#0e7490]" : "border-slate-300"}`}>
            <span className={`h-2.5 w-2.5 rounded-full ${c} ${t.online && t.wlc_ok ? "animate-pulse" : ""}`} data-testid={`spin-light-dot-${t.name}`} />
            <span className="text-slate-700">{t.name}</span><span className="text-slate-500" data-testid={`spin-light-label-${t.name}`}>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SpinTesterButton({ disk, body, clientId, onReadings }) {
  const [terminal, setTerminal] = useState(() => localStorage.getItem(TERM_KEY) || "");
  const [terminals, setTerminals] = useState([]);
  const [showConnect, setShowConnect] = useState(false);
  const [job, setJob] = useState(null);
  const poll = useRef(null);
  useEffect(() => () => clearInterval(poll.current), []);
  useEffect(() => {
    const load = () => api("GET", "/spin/terminals").then(setTerminals).catch(() => {});
    load(); const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);
  const [focus, setFocus] = useState(null);

  const start = async () => {
    const list = await api("GET", "/spin/terminals").catch(() => []);
    setTerminals(list);
    let t = terminal && list.find((x) => x.name === terminal) ? terminal : "";
    const ready = list.filter((x) => x.online);
    if (!t && ready.length === 1) { t = ready[0].name; }
    if (!t && list.length === 1) { t = list[0].name; }
    if (t && t !== terminal) { localStorage.setItem(TERM_KEY, t); setTerminal(t); }
    const cur = list.find((x) => x.name === t);
    if (!cur || !cur.online) { setShowConnect(true); return; }
    try {
      const j = await api("POST", "/spin/jobs", { terminal: t, disk, sample: sampleFor(body?.type), client_id: clientId, body_id: body?.id });
      setJob({ ...j, startedAt: Date.now() });
      clearInterval(poll.current);
      poll.current = setInterval(async () => {
        const s = await api("GET", `/spin/jobs/${j.id}`).catch(() => null);
        if (!s || s.status === "queued" || s.status === "running") { if (s) setJob((p) => p && { ...p, status: s.status }); return; }
        clearInterval(poll.current); setJob(null);
        if (s.status === "done") { onReadings(Object.fromEntries(Object.entries(s.readings || {}).filter(([k]) => k !== "salt" && k !== "tds").map(([k, v]) => [k, String(v)]))); toast.success(`Spin Touch results loaded (Disk ${disk}). Saved automatically`); }
        else toast.error(s.error || "Spin test failed");
      }, 2000);
    } catch (e) { toast.error(e.message, { action: { label: "Setup", onClick: () => setShowConnect(true) } }); }
  };
  const cancel = () => { clearInterval(poll.current); setJob(null); };

  return (
    <>
      <div className="flex gap-1">
        <button onClick={start} disabled={!!job} data-testid="wt-run-lamotte" className="flex-1 py-1 rounded-sm bg-[#0e7490] text-white font-black text-[13px] hover:bg-[#0b5e75] disabled:opacity-70 flex items-center justify-center gap-2">
          <Play className="h-4 w-4" fill="#fff" /> Test with LaMotte Spin Tester (Disk {disk}){terminal ? ` · ${terminal}` : ""}
        </button>
        <button onClick={async () => { setTerminals(await api("GET", "/spin/terminals").catch(() => [])); setShowConnect(true); }} data-testid="wt-spin-setup" title="Spin Tester setup" className="px-2 rounded-sm border border-[#0e7490] text-[#0e7490] bg-white hover:bg-[#e6f4f7]"><Monitor className="h-4 w-4" /></button>
      </div>
      <div className="mt-1"><SpinLights terminals={terminals} mine={terminal} onOpen={(n) => { setFocus(n); setShowConnect(true); }} /></div>
      {job && (
        <div className="fixed inset-0 z-[70] bg-slate-900/50 flex items-center justify-center p-3" data-testid="spin-running">
          <div className="bg-white rounded-xl border border-[#7ba7d9] shadow-2xl p-6 w-full max-w-sm text-center space-y-3">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-[#0e7490]" />
            <div className="font-black text-[#123a63]">{job.status === "running" ? `Spinning Disk ${disk}…` : `Sending to ${job.terminal}…`}</div>
            <p className="text-[12px] text-slate-600">{job.status === "running" ? "Takes about 60–90 seconds. Results drop straight into this test." : "Waiting for the Spin Touch bridge to pick up the test."}</p>
            <button onClick={cancel} data-testid="spin-cancel" className="px-4 py-1 rounded-sm border border-slate-300 text-slate-600 text-[12px] font-bold hover:bg-slate-50">Close</button>
          </div>
        </div>
      )}
      {showConnect && <ConnectPanel terminals={terminals} current={focus ?? terminal} onPick={(n) => { setTerminal(n); setFocus(null); setShowConnect(false); toast.success(`This computer will use the '${n}' Spin Tester`); }} onClose={() => { setFocus(null); setShowConnect(false); }} />}
    </>
  );
}

